import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import { useCallback, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link, useHistory } from "react-router-dom";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";
import { useLocaleProvider } from "../../../components/locale-provider";
import { useLoyaltyBrand } from "../../../components/loyalty-brand-provider";
import { useProperty } from "../../../components/property-provider";
import {
  DefaultTableRowActionsProps,
  Delete,
  Edit,
  StyledReactTooltip,
} from "../../shared/actions/tableActions";
import CrudList, { TableColumns } from "../../shared/crud/list";
import { StringRenderer } from "../../shared/renderers/table";
import { LoyaltyTagTableDto } from "../infrastructure/dto/LoyaltyTagTableDto";
import { useTags } from "../infrastructure/hooks/tags.hook";
import { LoyaltyTagMapper } from "../infrastructure/mappers/LoyaltyTagMapper";
import { loyaltyTagsListMessages, loyaltyTagsMessages } from "./LoyaltyTags.messages";

const columns: TableColumns<LoyaltyTagTableDto> = {
  ticker: {
    renderer: StringRenderer,
  },
  name: {
    renderer: StringRenderer,
  },
};

function TableActions() {
  const { ticker } = useProperty();
  return (
    <div className="flex gap-4">
      <Link className="btn btn-primary" to="preferences/new">
        <FormattedMessage {...loyaltyTagsMessages.newPreference} />
      </Link>
      <Link className="btn btn-primary" to="preferences/excel">
        <FormattedMessage {...loyaltyTagsMessages.newPreferencesExcel} />
      </Link>
      <Link className="btn btn-secondary" to={`/${ticker}/loyalty-preferences`}>
        <FormattedMessage {...loyaltyTagsMessages.goBack} />
      </Link>
    </div>
  );
}

function ILoyaltyTags() {
  const brandId = useLoyaltyBrand()?.externalSystemId || "";
  const { currentLocale } = useLocaleProvider();
  const history = useHistory();

  const [forceUpdate, setForceUpdate] = useState(0);
  const { getTags, deleteTag } = useTags(brandId);

  const handleOnEdit = useCallback((id: string) => history.push(`preferences/edit/${id}`), [
    history,
  ]);
  const handleReload = useCallback(() => setForceUpdate(prev => prev + 1), []);

  const handleGetAll = useCallback(async () => {
    const tags = await getTags();
    return tags.map(it => LoyaltyTagMapper.toTableDto(it, currentLocale));
  }, [currentLocale, getTags]);

  const RowActions = useMemo(
    () => ({ row, rest }: DefaultTableRowActionsProps<LoyaltyTagTableDto>) => {
      return (
        <Table.Cell {...rest} style={{ zIndex: 999 }}>
          <StyledReactTooltip id="edit" effect="solid">
            <FormattedMessage {...loyaltyTagsMessages.edit} />
          </StyledReactTooltip>

          <Edit row={row} onGoToEdit={handleOnEdit} />

          <Delete
            rest={rest}
            row={row}
            onDelete={deleteTag}
            onReloadList={handleReload}
            useOverlay={true}
          />
        </Table.Cell>
      );
    },
    [deleteTag, handleOnEdit, handleReload]
  );

  if (!brandId) return null;

  return (
    <CrudList
      columns={columns}
      onGetAll={handleGetAll}
      actions={TableActions}
      rowActions={RowActions}
      forceLoad={forceUpdate}
      messages={loyaltyTagsListMessages}
      hideExportButton
    />
  );
}

export const LoyaltyTags = withAuthorizationVerifier(ILoyaltyTags);
